import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { prescriptionAPI, patientAPI, doctorAPI, csrfHeaders } from '../../lib/api';
import { formatDateTime, getStatusColor, apiErrorMessage } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { FileText, Plus, Trash2, Eye, Printer } from 'lucide-react';
import { toast } from 'sonner';
import DrawingPad from '../../components/DrawingPad';
import SearchableSelect from '../../components/SearchableSelect/SearchableSelect';
import SuggestItemModal from '../../components/SuggestItem/SuggestItemModal';
import { logger } from '../../lib/logger';

let medicineKeySeq = 0;
const newMedicine = () => ({ _key: `med-${++medicineKeySeq}`, medicine_name: '', dosage: '', frequency: '', duration: '', instructions: '' });

const PrescriptionList = () => {
  const { user, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [prescriptions, setPrescriptions] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientHistory, setPatientHistory] = useState([]);
  
  // Get patient_id from URL query params
  const queryParams = new URLSearchParams(location.search);
  const patientIdFromUrl = queryParams.get('patient_id');
  
  const [formData, setFormData] = useState({
    patient_id: patientIdFromUrl || '',
    doctor_id: '',
    diagnosis: '',
    medicines: [newMedicine()],
    advice: '',
  });
  const [drawingData, setDrawingData] = useState(null);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [currentMedicineIndex, setCurrentMedicineIndex] = useState(null);

  useEffect(() => {
    // If patient_id in URL, auto-open dialog and fetch patient history
    if (patientIdFromUrl) {
      fetchPatientHistory(patientIdFromUrl);
      setDialogOpen(true);
    }
  }, [patientIdFromUrl]);

  const fetchData = useCallback(async () => {
    try {
      const params = patientIdFromUrl ? { patient_id: patientIdFromUrl } : {};
      const [presRes, patRes, docRes] = await Promise.all([
        prescriptionAPI.getAll(params),
        patientAPI.getAll({}),
        doctorAPI.getAll(),
      ]);
      
      // Handle paginated response structures
      setPrescriptions(presRes.data.prescriptions || presRes.data || []);
      setPatients(patRes.data.patients || patRes.data || []);
      setDoctors(docRes.data.doctors || docRes.data || []);
      
      // If patient_id in URL, set selected patient
      if (patientIdFromUrl) {
        const patientsArray = patRes.data.patients || patRes.data || [];
        const patient = patientsArray.find(p => p.id === patientIdFromUrl);
        setSelectedPatient(patient);
      }
    } catch (error) {
      logger.error('Error loading prescription data:', error);
      toast.error(apiErrorMessage(error, 'Failed to load prescription data. Please try again.'));
      // Set empty arrays to prevent crashes
      setPrescriptions([]);
      setPatients([]);
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  }, [patientIdFromUrl]);


  useEffect(() => {
    fetchData();
  }, [fetchData]);
  const fetchPatientHistory = async (patientId) => {
    try {
      const response = await prescriptionAPI.getAll({ patient_id: patientId });
      setPatientHistory(response.data.prescriptions || response.data || []);
    } catch (error) {
      logger.error('Failed to load patient history:', error);
      setPatientHistory([]);
    }
  };

  const addMedicine = () => {
    setFormData({
      ...formData,
      medicines: [...formData.medicines, newMedicine()],
    });
  };

  const removeMedicine = (index) => {
    const newMedicines = formData.medicines.filter((_, i) => i !== index);
    setFormData({ ...formData, medicines: newMedicines });
  };

  const updateMedicine = (index, field, value) => {
    const newMedicines = [...formData.medicines];
    newMedicines[index][field] = value;
    setFormData({ ...formData, medicines: newMedicines });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        medicines: formData.medicines.map(({ _key, ...med }) => med),
        doctor_id: hasRole('doctor') ? user.id : formData.doctor_id,
      };
      
      // Auto-create suggestions for new medicines (non-blocking)
      const medicines = formData.medicines || [];
      medicines.forEach(async (med) => {
        if (med.medicine_name && med.medicine_name.trim()) {
          try {
            // Check if medicine exists by searching
            const searchResponse = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/medications/search?q=${encodeURIComponent(med.medicine_name)}`,
              { credentials: 'include' }
            );
            const searchData = await searchResponse.json();
            const exists = searchData.results?.some(
              item => item.name.toLowerCase() === med.medicine_name.toLowerCase()
            );
            
            // If doesn't exist, auto-create suggestion
            if (!exists) {
              await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/suggest-master-item`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
                body: JSON.stringify({
                  item_type: 'medication',
                  item_name: med.medicine_name.trim(),
                  item_details: {
                    description: `Auto-suggested from prescription`
                  }
                })
              });
            }
          } catch (err) {
            // Silent fail - don't block prescription creation
            logger.debug('Auto-suggestion failed for:', med.medicine_name);
          }
        }
      });
      
      await prescriptionAPI.create(data);
      toast.success('Prescription created and sent to Pharmacy');
      setDialogOpen(false);
      
      // If came from patient detail, navigate back to patient detail page
      if (patientIdFromUrl) {
        navigate(`/patients/${patientIdFromUrl}`);
      } else {
        setFormData({
          patient_id: '',
          doctor_id: '',
          diagnosis: '',
          medicines: [newMedicine()],
          advice: '',
        });
        fetchData();
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to create prescription'));
    }
  };

  const viewPrescription = (prescription) => {
    setSelectedPrescription(prescription);
    setViewDialogOpen(true);
  };

  return (
    <div data-testid="prescriptions-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Prescriptions</h1>
            <p className="text-slate-500">Manage patient prescriptions</p>
          </div>
          {hasRole(['super_admin', 'admin', 'doctor']) && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open && patientIdFromUrl) {
                // When dialog is closed (X button clicked), navigate back
                navigate(`/patients/${patientIdFromUrl}`);
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-red-500 hover:bg-red-600 rounded-full" data-testid="new-prescription-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Prescription
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-[Manrope]">
                    {selectedPatient ? `Prescription for ${selectedPatient.full_name}` : 'Create Prescription'}
                  </DialogTitle>
                </DialogHeader>

                {/* Patient History Section */}
                {selectedPatient && patientHistory.length > 0 && (
                  <Card className="mt-4">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-semibold">Previous Prescriptions</CardTitle>
                    </CardHeader>
                    <CardContent className="max-h-48 overflow-y-auto">
                      <div className="space-y-2">
                        {patientHistory.slice(0, 3).map((presc) => (
                          <div key={presc.id} className="p-2 bg-slate-50 rounded-md text-xs">
                            <p className="font-medium">{formatDateTime(presc.created_at)}</p>
                            <p className="text-slate-600">
                              {presc.medicines?.map(m => m.medicine_name).join(', ')}
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  {!patientIdFromUrl && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Patient *</Label>
                        <Select value={formData.patient_id} onValueChange={(v) => { setFormData({...formData, patient_id: v}); fetchPatientHistory(v); }}>
                          <SelectTrigger data-testid="select-patient">
                            <SelectValue placeholder="Select patient" />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={5}>
                            {patients.map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.full_name} ({p.patient_id})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {!hasRole('doctor') && (
                        <div>
                          <Label>Doctor *</Label>
                          <Select value={formData.doctor_id} onValueChange={(v) => setFormData({...formData, doctor_id: v})}>
                            <SelectTrigger data-testid="select-doctor">
                              <SelectValue placeholder="Select doctor" />
                            </SelectTrigger>
                            <SelectContent position="popper" sideOffset={5}>
                              {doctors.map(d => (
                                <SelectItem key={d.id} value={d.id}>Dr. {d.full_name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}

                  {patientIdFromUrl && selectedPatient && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-800">
                        <strong>Patient:</strong> {selectedPatient.full_name} ({selectedPatient.patient_id})
                      </p>
                    </div>
                  )}

                  <div>
                    <Label>Diagnosis *</Label>
                    <Textarea
                      value={formData.diagnosis}
                      onChange={(e) => setFormData({...formData, diagnosis: e.target.value})}
                      placeholder="Enter diagnosis"
                      required
                      data-testid="input-diagnosis"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Medicines</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addMedicine}>
                        <Plus className="w-3 h-3 mr-1" /> Add Medicine
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {formData.medicines.map((med, index) => (
                        <div key={med._key} className="p-3 bg-slate-50 rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">Medicine {index + 1}</span>
                            {index > 0 && (
                              <Button type="button" variant="ghost" size="sm" onClick={() => removeMedicine(index)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <SearchableSelect
                                value={med.medicine_name}
                                onValueChange={(v) => updateMedicine(index, 'medicine_name', v)}
                                onSuggestNew={(query) => {
                                  setCurrentMedicineIndex(index);
                                  setShowSuggestModal(true);
                                }}
                                searchEndpoint={`${process.env.REACT_APP_BACKEND_URL}/api/medications/search`}
                                placeholder="Search or type medicine..."
                                searchPlaceholder="Search or type new medicine..."
                                itemType="medication"
                                allowFreeText={true}
                              />
                            </div>
                            <Input
                              placeholder="Dosage (e.g., 500mg)"
                              value={med.dosage}
                              onChange={(e) => updateMedicine(index, 'dosage', e.target.value)}
                            />
                            <Input
                              placeholder="Frequency (e.g., Twice daily)"
                              value={med.frequency}
                              onChange={(e) => updateMedicine(index, 'frequency', e.target.value)}
                            />
                            <Input
                              placeholder="Duration (e.g., 5 days)"
                              value={med.duration}
                              onChange={(e) => updateMedicine(index, 'duration', e.target.value)}
                            />
                          </div>
                          <Input
                            placeholder="Special instructions"
                            value={med.instructions}
                            onChange={(e) => updateMedicine(index, 'instructions', e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label>Advice</Label>
                    <Textarea
                      value={formData.advice}
                      onChange={(e) => setFormData({...formData, advice: e.target.value})}
                      placeholder="General advice for patient"
                      data-testid="input-advice"
                    />
                  </div>

                  {/* Drawing Pad for Doctor's Notes */}
                  <DrawingPad
                    label="Doctor's Handwritten Notes (Optional)"
                    onSave={(data) => {
                      setDrawingData(data);
                      toast.success('Handwritten notes saved');
                    }}
                  />

                  <div className="flex justify-end gap-2 pt-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setDialogOpen(false);
                        if (patientIdFromUrl) {
                          navigate(`/patients/${patientIdFromUrl}`);
                        }
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-red-500 hover:bg-red-600" data-testid="submit-prescription">Create Prescription</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </header>

      <div className="page-content">
        <Card>
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
              <FileText className="w-5 h-5 text-red-500" />
              Prescription Records
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading prescriptions...</div>
            ) : prescriptions.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No prescriptions found.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Prescription ID</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Doctor</TableHead>
                    <TableHead>Diagnosis</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {prescriptions.map((pres) => (
                    <TableRow key={pres.id} data-testid={`prescription-row-${pres.id}`}>
                      <TableCell className="font-mono text-sm">{pres.prescription_id}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{pres.patient?.full_name}</p>
                          <p className="text-xs text-slate-500">{pres.patient?.patient_id}</p>
                        </div>
                      </TableCell>
                      <TableCell>Dr. {pres.doctor?.full_name}</TableCell>
                      <TableCell className="max-w-xs truncate">{pres.diagnosis}</TableCell>
                      <TableCell>{formatDateTime(pres.created_at)}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(pres.status)}>{pres.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => viewPrescription(pres)} data-testid={`view-${pres.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View Prescription Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl printable-section">
          <DialogHeader className="no-print">
            <div className="flex items-center justify-between">
              <DialogTitle className="font-[Manrope]">Prescription Details</DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.print()}
                className="no-print"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </DialogHeader>
          
          {/* Print Header */}
          <div className="hidden print:block print-header">
            <h1>HMC Buddy - Prescription</h1>
            <p>Healventure Hospital</p>
          </div>
          
          {selectedPrescription && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Patient</p>
                  <p className="font-medium">{selectedPrescription.patient?.full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Prescription ID</p>
                  <p className="font-mono">{selectedPrescription.prescription_id}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-500">Diagnosis</p>
                <p>{selectedPrescription.diagnosis}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-2">Medicines</p>
                <div className="space-y-2">
                  {selectedPrescription.medicines?.map((med, i) => (
                    <div key={`${med.medicine_name}-${med.dosage}`} className="p-3 bg-slate-50 rounded-lg">
                      <p className="font-medium">{med.medicine_name}</p>
                      <p className="text-sm text-slate-600">
                        {med.dosage} | {med.frequency} | {med.duration}
                      </p>
                      {med.instructions && <p className="text-sm text-slate-500 mt-1">{med.instructions}</p>}
                    </div>
                  ))}
                </div>
              </div>
              {selectedPrescription.advice && (
                <div>
                  <p className="text-sm text-slate-500">Advice</p>
                  <p>{selectedPrescription.advice}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Suggest New Medication Modal */}
      <SuggestItemModal
        open={showSuggestModal}
        onClose={() => setShowSuggestModal(false)}
        itemType="medication"
        onSuggestionCreated={() => {
          toast.info('Your medication suggestion has been submitted for approval');
        }}
      />
    </div>
  );
};

export default PrescriptionList;
